import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

const ViewerDashboard = () => {
  const { user } = usePPCAuth();
  const navigate = useNavigate();
  
  // Default to today's date
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  // Summary data from all dashboards
  const [bravoSummary, setBravoSummary] = useState(null);
  const [papaSummary, setPapaSummary] = useState(null);
  const [romeoSummary, setRomeoSummary] = useState(null);
  const [golfSummary, setGolfSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const showToast = (message, type = 'success') => { setToast({ message, type }); setTimeout(() => setToast(null), 3000); };

  // Fetch all summaries
  const fetchAllSummaries = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const [bravoRes, papaRes, romeoRes, golfRes] = await Promise.all([
        axios.get(`${PPC_API_URL}/bravo.php?action=summary&date=${selectedDate}`, getAuthHeader()).catch(() => ({ data: null })),
        axios.get(`${PPC_API_URL}/papa.php?action=summary&date=${selectedDate}`, getAuthHeader()).catch(() => ({ data: null })),
        axios.get(`${PPC_API_URL}/romeo.php?action=summary&date=${selectedDate}`, getAuthHeader()).catch(() => ({ data: null })),
        axios.get(`${PPC_API_URL}/golf.php?action=summary&date=${selectedDate}`, getAuthHeader()).catch(() => ({ data: null }))
      ]);
      
      if (bravoRes.data?.success) setBravoSummary(bravoRes.data);
      else setBravoSummary(null);
      
      if (papaRes.data?.success) setPapaSummary(papaRes.data);
      else setPapaSummary(null);
      
      if (romeoRes.data?.success) setRomeoSummary(romeoRes.data);
      else setRomeoSummary(null);
      
      if (golfRes.data?.success) setGolfSummary(golfRes.data);
      else setGolfSummary(null);
      
    } catch (err) {
      console.error('Failed to fetch summaries:', err);
      showToast('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchAllSummaries();
  }, [fetchAllSummaries]);

  // Calculate totals
  const bravoTotal = bravoSummary?.dayTotalQty || 0;
  const bravoHlTotal = bravoSummary?.dayTotalHlQty || 0;
  const bravoTokens = bravoSummary?.dayTotalTokens || 0;
  const bravoAssociates = bravoSummary?.dayTotalAssociates || 0;
  
  const papaTotal = papaSummary?.dayTotalOutput || 0;
  const papaTokens = papaSummary?.dayTotalTokens || 0;
  const papaAssociates = (papaSummary?.dayTotalPeeling || 0) + (papaSummary?.dayTotalSupporting || 0);
  
  const romeoBoxes = romeoSummary?.dayTotalBoxes || 0;
  const romeoAssociates = romeoSummary?.dayTotalAssociates || 0;
  
  const golfLots = golfSummary?.dayTotalLots || 0;
  const golfHlQty = golfSummary?.dayTotalHlQty || 0;
  const golfAssociates = golfSummary?.dayTotalAssociates || 0;

  // Grand totals
  const totalAssociates = bravoAssociates + papaAssociates + romeoAssociates + golfAssociates;
  const totalTokens = bravoTokens + papaTokens;
  const totalHlQty = bravoHlTotal + golfHlQty;

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    
    // Overall Summary Sheet
    const overallData = [
      ['DAILY PRODUCTION SUMMARY'],
      ['Date:', selectedDate],
      ['Generated:', new Date().toLocaleString()],
      [''],
      ['DEPARTMENT', 'OUTPUT', 'TOKENS', 'ASSOCIATES', 'ADDITIONAL INFO'],
      ['BRAVO (H.ON Processing)', `${bravoTotal.toFixed(2)} KG`, bravoTokens, bravoAssociates, `H.L: ${bravoHlTotal.toFixed(2)} KG`],
      ['PAPA (Peeling)', `${papaTotal.toFixed(2)} KG`, papaTokens, papaAssociates, ''],
      ['ROMEO (Unloading)', `${romeoBoxes} Boxes`, '-', romeoAssociates, ''],
      ['GOLF (LOT Management)', `${golfLots} Lots`, '-', golfAssociates, `H.L: ${golfHlQty.toFixed(2)} KG`],
      [''],
      ['GRAND TOTALS'],
      ['Total Associates:', totalAssociates],
      ['Total Tokens:', totalTokens],
      ['Total H.L Qty:', `${totalHlQty.toFixed(2)} KG`]
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(overallData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Overall Summary');
    
    // BRAVO Details
    if (bravoSummary?.contractorSummary) {
      const bravoData = [
        ['BRAVO - Contractor Summary'],
        ['Date:', selectedDate],
        [''],
        ['Contractor', 'Tokens', 'Associates', 'Day Qty', 'Night Qty', 'Total Qty', 'H.L Qty']
      ];
      bravoSummary.contractorSummary.forEach(c => {
        bravoData.push([c.contractor, c.tokens || 0, c.total_associates || 0, 
          parseFloat(c.day_qty || 0).toFixed(2), parseFloat(c.night_qty || 0).toFixed(2),
          parseFloat(c.total_qty || 0).toFixed(2), parseFloat(c.total_hl_qty || 0).toFixed(2)]);
      });
      const ws2 = XLSX.utils.aoa_to_sheet(bravoData);
      XLSX.utils.book_append_sheet(wb, ws2, 'BRAVO Details');
    }
    
    // PAPA Details
    if (papaSummary?.contractorSummary) {
      const papaData = [
        ['PAPA - Contractor Summary'],
        ['Date:', selectedDate],
        [''],
        ['Contractor', 'Tokens', 'Peeling', 'Supporting', 'Day Output', 'Night Output', 'Total Output']
      ];
      papaSummary.contractorSummary.forEach(c => {
        papaData.push([c.contractor, c.tokens || 0, c.peeling_associates || 0, c.supporting_associates || 0,
          parseFloat(c.day_output || 0).toFixed(2), parseFloat(c.night_output || 0).toFixed(2),
          parseFloat(c.total_output || 0).toFixed(2)]);
      });
      const ws3 = XLSX.utils.aoa_to_sheet(papaData);
      XLSX.utils.book_append_sheet(wb, ws3, 'PAPA Details');
    }
    
    // ROMEO Details
    if (romeoSummary?.contractorSummary) {
      const romeoData = [
        ['ROMEO - Contractor Summary'],
        ['Date:', selectedDate],
        [''],
        ['Contractor', 'Male', 'Female', 'Total Associates', 'Day Boxes', 'Night Boxes', 'Total Boxes']
      ];
      romeoSummary.contractorSummary.forEach(c => {
        romeoData.push([c.contractor, c.male_associates || 0, c.female_associates || 0, c.total_associates || 0,
          c.day_boxes || 0, c.night_boxes || 0, c.total_boxes || 0]);
      });
      const ws4 = XLSX.utils.aoa_to_sheet(romeoData);
      XLSX.utils.book_append_sheet(wb, ws4, 'ROMEO Details');
    }
    
    // GOLF Details
    if (golfSummary?.sectionSummary) {
      const golfData = [
        ['GOLF - Section Summary'],
        ['Date:', selectedDate],
        [''],
        ['Section', 'Male', 'Female', 'Total Associates', 'Total Lots', 'H.L Qty (KG)']
      ];
      golfSummary.sectionSummary.forEach(c => {
        golfData.push([c.golf_section, c.male_associates || 0, c.female_associates || 0, c.total_associates || 0,
          c.total_lots || 0, parseFloat(c.total_hl_quantity || 0).toFixed(2)]);
      });
      const ws5 = XLSX.utils.aoa_to_sheet(golfData);
      XLSX.utils.book_append_sheet(wb, ws5, 'GOLF Details');
    }
    
    XLSX.writeFile(wb, `DailyProductionSummary_${selectedDate}.xlsx`);
    showToast('Report exported successfully!');
  };

  return (
    <div className="viewer-dashboard">
      {/* Sticky Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/ppc')}>← Back</button>
          <h1 className="dashboard-title" style={{ fontSize: '24px', fontWeight: '700' }}>
            📊 DAILY PRODUCTION SUMMARY
          </h1>
          <span className="user-role-badge">
            👁️ Viewer
          </span>
        </div>
        <div className="header-right">
          <button className="export-btn" onClick={exportToExcel} style={{
            padding: '8px 16px', border: '1px solid rgba(255,255,255,.28)', borderRadius: '6px',
            background: 'rgba(255,255,255,.16)', color: 'white',
            cursor: 'pointer', marginRight: '10px', fontSize: '13px', fontWeight: '600'
          }}>📥 Export Report</button>
          <input 
            type="date" 
            className="date-picker" 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </header>

      <main className="dashboard-content" style={{ padding: '20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p>Loading data...</p>
          </div>
        ) : (
          <>
            {/* Date Display */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', color: '#374151', fontWeight: '600' }}>
                📅 {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h2>
            </div>

            {/* Grand Total Cards */}
            <section className="grand-totals" style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>📈 Overall Totals</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div className="grand-card" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', padding: '24px', borderRadius: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>👥 TOTAL ASSOCIATES</div>
                  <div style={{ fontSize: '36px', fontWeight: '700' }}>{totalAssociates}</div>
                </div>
                <div className="grand-card" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', padding: '24px', borderRadius: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>🎫 TOTAL TOKENS</div>
                  <div style={{ fontSize: '36px', fontWeight: '700' }}>{totalTokens}</div>
                </div>
                <div className="grand-card" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', padding: '24px', borderRadius: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>📦 TOTAL H.L QTY</div>
                  <div style={{ fontSize: '36px', fontWeight: '700' }}>{totalHlQty.toFixed(2)}</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>KG</div>
                </div>
              </div>
            </section>

            {/* Department Cards */}
            <section className="department-summary" style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>🏭 Department Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                
                {/* BRAVO Card */}
                <div className="dept-card" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', padding: '16px 20px', color: 'white' }}>
                    <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>🔵 BRAVO</h4>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.9 }}>H.ON Processing</p>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>H.ON Quantity</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>{bravoTotal.toFixed(2)} <small style={{ fontSize: '12px' }}>KG</small></div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>H.L Quantity</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>{bravoHlTotal.toFixed(2)} <small style={{ fontSize: '12px' }}>KG</small></div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Tokens</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#f59e0b' }}>{bravoTokens}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Associates</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#8b5cf6' }}>{bravoAssociates}</div></div>
                    </div>
                    {bravoSummary?.contractorSummary && (
                      <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>Active Contractors: {bravoSummary.contractorSummary.filter(c => parseFloat(c.total_qty) > 0).length}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {bravoSummary.contractorSummary.filter(c => parseFloat(c.total_qty) > 0).map((c, i) => (
                            <span key={i} style={{ fontSize: '10px', background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '10px' }}>{c.contractor}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* PAPA Card */}
                <div className="dept-card" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)', padding: '16px 20px', color: 'white' }}>
                    <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>🟠 PAPA</h4>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.9 }}>Peeling Operations</p>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Total Output</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#f97316' }}>{papaTotal.toFixed(2)} <small style={{ fontSize: '12px' }}>KG</small></div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Tokens</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>{papaTokens}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Peeling</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#8b5cf6' }}>{papaSummary?.dayTotalPeeling || 0}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Supporting</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#6b7280' }}>{papaSummary?.dayTotalSupporting || 0}</div></div>
                    </div>
                    {papaSummary?.contractorSummary && (
                      <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>Active Contractors: {papaSummary.contractorSummary.filter(c => parseFloat(c.total_output) > 0).length}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {papaSummary.contractorSummary.filter(c => parseFloat(c.total_output) > 0).map((c, i) => (
                            <span key={i} style={{ fontSize: '10px', background: '#ffedd5', color: '#c2410c', padding: '2px 8px', borderRadius: '10px' }}>{c.contractor}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ROMEO Card */}
                <div className="dept-card" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', padding: '16px 20px', color: 'white' }}>
                    <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>🟢 ROMEO</h4>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.9 }}>Boxes Unloading</p>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Total Boxes</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>{romeoBoxes}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Associates</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>{romeoAssociates}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Male</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#3b82f6' }}>{romeoSummary?.contractorSummary?.reduce((s, c) => s + (parseInt(c.male_associates) || 0), 0) || 0}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Female</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#ec4899' }}>{romeoSummary?.contractorSummary?.reduce((s, c) => s + (parseInt(c.female_associates) || 0), 0) || 0}</div></div>
                    </div>
                    {romeoSummary?.contractorSummary && (
                      <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>Active Sections: {romeoSummary.contractorSummary.filter(c => parseInt(c.total_boxes) > 0).length}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {romeoSummary.contractorSummary.filter(c => parseInt(c.total_boxes) > 0).map((c, i) => (
                            <span key={i} style={{ fontSize: '10px', background: '#d1fae5', color: '#047857', padding: '2px 8px', borderRadius: '10px' }}>{c.contractor}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* GOLF Card */}
                <div className="dept-card" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', padding: '16px 20px', color: 'white' }}>
                    <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>🟣 GOLF</h4>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.9 }}>LOT Management</p>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Total Lots</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>{golfLots}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>H.L Qty</span><div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>{golfHlQty.toFixed(2)} <small style={{ fontSize: '12px' }}>KG</small></div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Associates</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#f59e0b' }}>{golfAssociates}</div></div>
                      <div><span style={{ fontSize: '12px', color: '#6b7280' }}>Sections</span><div style={{ fontSize: '20px', fontWeight: '600', color: '#6b7280' }}>{golfSummary?.sectionSummary?.length || 0}</div></div>
                    </div>
                    {golfSummary?.sectionSummary && (
                      <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {golfSummary.sectionSummary.map((c, i) => (
                            <span key={i} style={{ fontSize: '10px', background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: '10px' }}>{c.golf_section}: {c.total_lots} lots</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Analytics Section */}
            <section className="analytics-section" style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>📊 Analytics & Distribution</h3>
              
              {/* Production Distribution Bar */}
              <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>Associates Distribution</h4>
                <div style={{ display: 'flex', height: '40px', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px' }}>
                  {totalAssociates > 0 && (
                    <>
                      <div style={{ width: `${(bravoAssociates/totalAssociates)*100}%`, background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '600', minWidth: bravoAssociates > 0 ? '40px' : '0' }}>
                        {bravoAssociates > 0 && bravoAssociates}
                      </div>
                      <div style={{ width: `${(papaAssociates/totalAssociates)*100}%`, background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '600', minWidth: papaAssociates > 0 ? '40px' : '0' }}>
                        {papaAssociates > 0 && papaAssociates}
                      </div>
                      <div style={{ width: `${(romeoAssociates/totalAssociates)*100}%`, background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '600', minWidth: romeoAssociates > 0 ? '40px' : '0' }}>
                        {romeoAssociates > 0 && romeoAssociates}
                      </div>
                      <div style={{ width: `${(golfAssociates/totalAssociates)*100}%`, background: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '600', minWidth: golfAssociates > 0 ? '40px' : '0' }}>
                        {golfAssociates > 0 && golfAssociates}
                      </div>
                    </>
                  )}
                  {totalAssociates === 0 && (
                    <div style={{ width: '100%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: '12px' }}>No Data</div>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '12px', height: '12px', background: '#3b82f6', borderRadius: '2px' }}></span>BRAVO ({bravoAssociates})</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '12px', height: '12px', background: '#f97316', borderRadius: '2px' }}></span>PAPA ({papaAssociates})</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '12px', height: '12px', background: '#10b981', borderRadius: '2px' }}></span>ROMEO ({romeoAssociates})</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '12px', height: '12px', background: '#8b5cf6', borderRadius: '2px' }}></span>GOLF ({golfAssociates})</div>
                </div>
              </div>

              {/* Key Metrics Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Avg Associates/Dept</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>{(totalAssociates / 4).toFixed(1)}</div>
                </div>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>BRAVO Productivity</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>{bravoAssociates > 0 ? (bravoTotal / bravoAssociates).toFixed(1) : '0'} <small style={{ fontSize: '12px', color: '#6b7280' }}>KG/person</small></div>
                </div>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>PAPA Productivity</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#f97316' }}>{papaAssociates > 0 ? (papaTotal / papaAssociates).toFixed(1) : '0'} <small style={{ fontSize: '12px', color: '#6b7280' }}>KG/person</small></div>
                </div>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>ROMEO Efficiency</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>{romeoAssociates > 0 ? (romeoBoxes / romeoAssociates).toFixed(1) : '0'} <small style={{ fontSize: '12px', color: '#6b7280' }}>boxes/person</small></div>
                </div>
              </div>
            </section>

            {/* Detailed Tables */}
            <section className="detailed-tables">
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>📋 Detailed Breakdown</h3>
              
              {/* BRAVO Table */}
              {bravoSummary?.contractorSummary && bravoSummary.contractorSummary.length > 0 && (
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#3b82f6' }}>🔵 BRAVO - Contractor Details</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Contractor</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Tokens</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Associates</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Day Qty</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Night Qty</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Total Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bravoSummary.contractorSummary.map((c, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>{c.contractor}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.tokens || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.total_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{parseFloat(c.day_qty || 0).toFixed(2)}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{parseFloat(c.night_qty || 0).toFixed(2)}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#3b82f6' }}>{parseFloat(c.total_qty || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f1f5f9', fontWeight: '600' }}>
                          <td style={{ padding: '10px' }}>TOTAL</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{bravoTokens}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{bravoAssociates}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{(bravoSummary?.dayTotalDayQty || 0).toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{(bravoSummary?.dayTotalNightQty || 0).toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#3b82f6' }}>{bravoTotal.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* PAPA Table */}
              {papaSummary?.contractorSummary && papaSummary.contractorSummary.length > 0 && (
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#f97316' }}>🟠 PAPA - Contractor Details</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Contractor</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Tokens</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Peeling</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Supporting</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Day Output</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Night Output</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Total Output</th>
                        </tr>
                      </thead>
                      <tbody>
                        {papaSummary.contractorSummary.map((c, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>{c.contractor}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.tokens || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.peeling_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.supporting_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{parseFloat(c.day_output || 0).toFixed(2)}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{parseFloat(c.night_output || 0).toFixed(2)}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#f97316' }}>{parseFloat(c.total_output || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f1f5f9', fontWeight: '600' }}>
                          <td style={{ padding: '10px' }}>TOTAL</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{papaTokens}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{papaSummary?.dayTotalPeeling || 0}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{papaSummary?.dayTotalSupporting || 0}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{(papaSummary?.dayTotalDayOutput || 0).toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{(papaSummary?.dayTotalNightOutput || 0).toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#f97316' }}>{papaTotal.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* ROMEO Table */}
              {romeoSummary?.contractorSummary && romeoSummary.contractorSummary.length > 0 && (
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#10b981' }}>🟢 ROMEO - Section Details</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Section</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Male</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Female</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Total</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Day Boxes</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Night Boxes</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Total Boxes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {romeoSummary.contractorSummary.map((c, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>{c.contractor}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.male_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.female_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.total_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{c.day_boxes || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{c.night_boxes || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#10b981' }}>{c.total_boxes || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f1f5f9', fontWeight: '600' }}>
                          <td style={{ padding: '10px' }}>TOTAL</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{romeoSummary.contractorSummary.reduce((s, c) => s + (parseInt(c.male_associates) || 0), 0)}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{romeoSummary.contractorSummary.reduce((s, c) => s + (parseInt(c.female_associates) || 0), 0)}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{romeoAssociates}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{romeoSummary?.dayTotalDayBoxes || 0}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{romeoSummary?.dayTotalNightBoxes || 0}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#10b981' }}>{romeoBoxes}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* GOLF Table */}
              {golfSummary?.sectionSummary && golfSummary.sectionSummary.length > 0 && (
                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#8b5cf6' }}>🟣 GOLF - Section Details</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Section</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Male</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Female</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Total</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Total Lots</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>H.L Qty (KG)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {golfSummary.sectionSummary.map((c, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>{c.golf_section}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.male_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.female_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{c.total_associates || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{c.total_lots || 0}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#8b5cf6' }}>{parseFloat(c.total_hl_quantity || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f1f5f9', fontWeight: '600' }}>
                          <td style={{ padding: '10px' }}>TOTAL</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{golfSummary.sectionSummary.reduce((s, c) => s + (parseInt(c.male_associates) || 0), 0)}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{golfSummary.sectionSummary.reduce((s, c) => s + (parseInt(c.female_associates) || 0), 0)}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{golfAssociates}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{golfLots}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#8b5cf6' }}>{golfHlQty.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>

            {/* No Data Message */}
            {!bravoSummary && !papaSummary && !romeoSummary && !golfSummary && (
              <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                <h3 style={{ color: '#374151', marginBottom: '8px' }}>No Data Available</h3>
                <p style={{ color: '#6b7280' }}>No production data found for {selectedDate}</p>
              </div>
            )}
          </>
        )}
      </main>

      {toast && (
        <div style={{
          position: 'fixed', bottom: '20px', right: '20px', padding: '12px 24px',
          background: toast.type === 'error' ? '#ef4444' : '#10b981', color: 'white',
          borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', zIndex: 1000
        }}>
          {toast.message}
        </div>
      )}

      <style>{`
        .viewer-dashboard { min-height: 100vh; background: #f3f4f6; }
        .dashboard-header { display: flex; justify-content: space-between; align-items: center; }
        .header-left { display: flex; align-items: center; gap: 12px; }
        .header-right { display: flex; align-items: center; gap: 10px; }
        .back-btn { padding: 8px 16px; border: 2px solid #e5e7eb; background: white; border-radius: 6px; cursor: pointer; font-weight: 500; }
        .back-btn:hover { background: #f9fafb; }
        @media (max-width: 768px) {
          .dashboard-header { flex-direction: column; gap: 12px; padding: 12px !important; }
          .header-left, .header-right { width: 100%; justify-content: center; flex-wrap: wrap; }
        }
      `}</style>
    </div>
  );
};

export default ViewerDashboard;